import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  ORG_ROLE_ADMIN,
  ORG_ROLE_CREATOR_LITE,
  ORG_ROLE_GUEST,
} from 'constants/organizationRoles';
import { WORKSPACE_PLAN_STARTER_FREE } from 'constants/workspacePlans';
import { selectCurrentUser } from 'selectors/user';

import useDeepCompareEffect from './useDeepCompareEffect';
import { fetchTeamMembers } from '../creators/team-members';
import {
  fetchLibraryVideoCountsCreator,
  fetchRecentTeamVideosCreator,
} from '../creators/videosDashboard';
import {
  fetchBillingDetailsForWorkspace,
  fetchWorkspaceAdmins,
} from '../creators/workspace';
import { selectDashboardRecentTeamVideoList } from '../selectors/videoDashboard';

export const useGetSelectedWorkspaceWithFetchingStatus = () => {
  const [selectedWorkspace, setSelectedWorkspace] = useState({});
  const workspaces = useSelector(state => state.workspace.workspaces);
  const isFetching = useSelector(state => state.workspace.isFetching);
  const [isFetchingSelectedWorkspace, setIsFetchingSelectedWorkspace] =
    useState(isFetching);
  const currentUser = useSelector(selectCurrentUser);

  useDeepCompareEffect(() => {
    const selectedWorkspace = workspaces.find(
      workspace => workspace.isSelected
    );

    setSelectedWorkspace(selectedWorkspace || {});
    setIsFetchingSelectedWorkspace(isFetching);
  }, [currentUser, workspaces, isFetching]);

  return { selectedWorkspace, isFetching: isFetchingSelectedWorkspace };
};

// useGetSelectedWorkspace is a backwards-compatible way to get the selected workspace
// without fetching status–many parts of the codebase use Object.keys(selectedWorkspace)
// to inspect whether the workspace exists or not
/**
 * @returns {{id: String}}
 */
export const useGetSelectedWorkspace = () => {
  const selectedWorkspaceWithStatus =
    useGetSelectedWorkspaceWithFetchingStatus();

  return selectedWorkspaceWithStatus.selectedWorkspace;
};

export const useGetUserRoleForSelectedWorkspace = () => {
  const [userRole, setUserRole] = useState();

  const selectedWorkspace = useGetSelectedWorkspace();

  useEffect(() => {
    setUserRole(selectedWorkspace?.memberRole);
  }, [selectedWorkspace]);

  return userRole;
};

export const useCheckIfSdkSharedUser = () => {
  const [isSdkSharedUser, setIsSdkSharedUser] = useState(false);

  const selectedWorkspace = useGetSelectedWorkspace();

  useEffect(() => {
    setIsSdkSharedUser(selectedWorkspace?.memberRole === ORG_ROLE_GUEST);
  }, [selectedWorkspace]);

  return isSdkSharedUser;
};

export const useGetDefaultWorkspace = () => {
  const [defaultWorkspace, setDefaultWorkspace] = useState({});
  const workspaces = useSelector(state => state.workspace.workspaces);
  const currentUser = useSelector(selectCurrentUser);

  useEffect(() => {
    const defaultWorkspace = workspaces.find(
      workspace => workspace.id === currentUser.default_workspace_id.toString()
    );

    setDefaultWorkspace(defaultWorkspace || {});
  }, [currentUser, workspaces]);

  return defaultWorkspace;
};

export const useWorkspaceBillingDetails = () => {
  const dispatch = useDispatch();
  const selectedWorkspace = useGetSelectedWorkspace();
  const workspaceId = selectedWorkspace?.id;

  useEffect(() => {
    if (workspaceId) {
      dispatch(fetchBillingDetailsForWorkspace(workspaceId));
    }
  }, [dispatch, workspaceId]);

  return selectedWorkspace?.billing_details;
};

export const useGetWorkspaceLibraryVideoCounts = () => {
  const dispatch = useDispatch();
  const [videoCounts, setVideoCounts] = useState();

  const reducerCounts = useSelector(
    state => state.videos_dashboard.library_video_counts
  );

  useEffect(() => {
    if (Object.keys(reducerCounts).length > 0) {
      setVideoCounts(reducerCounts);

      return;
    }

    dispatch(fetchLibraryVideoCountsCreator());
  }, [dispatch, reducerCounts]);

  return videoCounts;
};

export const useGetWorkspaceRecentTeamVideos = (skip = false) => {
  const dispatch = useDispatch();
  const [teamVideos, setTeamVideos] = useState();

  const videos = useSelector(selectDashboardRecentTeamVideoList);

  useEffect(() => {
    if (skip) {
      return;
    }

    if (Object.keys(videos).length > 0) {
      setTeamVideos(videos);

      return;
    }

    dispatch(fetchRecentTeamVideosCreator());
  }, [dispatch, skip, videos]);

  return teamVideos;
};

export const useGetWorkspaceTeamMembers = () => {
  const dispatch = useDispatch();
  const reducerMembers = useSelector(state => {
    return state.teamMembers.members;
  });

  const { id: workspaceId } = useGetSelectedWorkspace();

  const fetchingMembers = useSelector(state => {
    return state.teamMembers.fetching;
  });

  useEffect(() => {
    if (reducerMembers.length > 0 || fetchingMembers || !workspaceId) {
      return;
    }

    dispatch(fetchTeamMembers(workspaceId));
  }, [dispatch, fetchingMembers, reducerMembers, workspaceId]);

  return reducerMembers;
};

export const useGetWorkspaceMemberRoles = () => {
  const selectedWorkspace = useGetSelectedWorkspace();

  if (!selectedWorkspace?.id) {
    return [];
  }

  return selectedWorkspace.memberRoles;
};

export const useGetRolesForMemberInvitations = () => {
  const memberRoles = useGetWorkspaceMemberRoles();

  return memberRoles.filter(role => role.inviteable).sort();
};

export const useGetSelectedWorkspaceAdmins = () => {
  const selectedWorkspace = useGetSelectedWorkspace();
  const admins = selectedWorkspace?.admins;
  const dispatch = useDispatch();

  useEffect(() => {
    if (!selectedWorkspace?.id || admins) {
      return;
    }

    dispatch(fetchWorkspaceAdmins(selectedWorkspace.id));
  }, [selectedWorkspace, admins, dispatch]);

  return admins;
};

export const useGetMemberVideoLimits = () => {
  const selectedWorkspace = useGetSelectedWorkspace();

  const limit = Number(selectedWorkspace?.memberLimits?.TOTAL_VIDEOS) || 0;

  if (limit <= 0) {
    return {};
  }

  const totalVideos =
    Number(selectedWorkspace?.counts?.videos?.total_member_created_videos) || 0;
  const remainingVideos = limit - totalVideos;
  const exceededLimit = remainingVideos <= 0;
  const nearLimit = remainingVideos === 1;

  return {
    nearLimit,
    exceededLimit,
    limit,
    remainingVideos,
    totalVideos,
  };
};

export const useGetCreatorLiteMemberLimits = () => {
  const currentUser = useSelector(selectCurrentUser);
  const selectedWorkspace = useGetSelectedWorkspace();
  const availableRoles = useGetWorkspaceMemberRoles();

  const creatorLiteMemberLimit =
    selectedWorkspace?.limits?.CREATOR_LITE_MEMBER_LIMIT || 0;

  let totalCreatorLites =
    selectedWorkspace?.counts?.users?.total_active_creator_lites || 0;

  // Admins count towards limit in starter free workspaces
  if (selectedWorkspace?.type === WORKSPACE_PLAN_STARTER_FREE) {
    totalCreatorLites +=
      selectedWorkspace?.counts?.users?.total_active_admins || 0;
  }

  const showBanner =
    selectedWorkspace?.type === WORKSPACE_PLAN_STARTER_FREE &&
    currentUser.role === ORG_ROLE_ADMIN &&
    availableRoles.some(r => r.value === ORG_ROLE_CREATOR_LITE) &&
    creatorLiteMemberLimit > 0 &&
    totalCreatorLites >= creatorLiteMemberLimit;

  return { showBanner, creatorLiteMemberLimit };
};

export const useUserInSameWorkspaceAsItem = item => {
  const selectedWorkspace = useGetSelectedWorkspace();

  if (item) {
    return selectedWorkspace?.id == item.organization_id;
  }

  return false;
};
